// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import { DislodgementRange } from '../utils/glucoseFiltering'

type Translate = (key: string, options?: any) => string

/**
 * Builds Plotly `shapes` and `annotations` that mark each hidden
 * suspected-sensor-dislodgement window with a gray band so vets/owners see the
 * data was intentionally withheld for safety review — rather than an
 * unexplained gap or, worse, a false sustained-low line.
 *
 * Open-ended ranges (`end: null`, sensor never recovered in the window) extend
 * to the right edge of the plot via a paper-relative x1.
 *
 * @param ranges Hidden ranges (Unix seconds) from useDeviceMetrics
 * @param translate react-admin translate fn for the band label/tooltip
 * @returns `{ shapes, annotations }` to spread into a Plotly layout
 */
export function dislodgementPlotShapes(
  ranges: DislodgementRange[],
  translate: Translate,
): { shapes: any[]; annotations: any[] } {
  if (!ranges || ranges.length === 0) {
    return { shapes: [], annotations: [] }
  }

  const label = translate('pos.glucose.suspected_detachment')
  const shapes: any[] = []
  const annotations: any[] = []

  for (const range of ranges) {
    const x0 = new Date(range.start * 1000)
    // When there is no recovery, extend the band to the end of the data window.
    // A far-future date keeps the band visible without paper/x-axis ref mixing.
    const x1 =
      range.end !== null
        ? new Date(range.end * 1000)
        : new Date(range.start * 1000 + 1000 * 60 * 60 * 24 * 400)

    shapes.push({
      type: 'rect' as const,
      xref: 'x' as const,
      yref: 'paper' as const,
      x0,
      x1,
      y0: 0,
      y1: 1,
      fillcolor: 'rgba(120, 120, 120, 0.18)',
      line: { width: 0 },
      layer: 'below' as const,
    })

    annotations.push({
      x: x0,
      xref: 'x' as const,
      y: 1,
      yref: 'paper' as const,
      yanchor: 'bottom' as const,
      xanchor: 'left' as const,
      text: label,
      showarrow: false,
      font: { size: 10, color: '#616161' },
      bgcolor: 'rgba(255,255,255,0.7)',
    })
  }

  return { shapes, annotations }
}
